import { Children, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A row of equal columns that scrolls sideways, with its scrollbar on top.
 *
 * Two modes, chosen from the width the row actually has:
 *
 *   - Enough room for every column at a comfortable width: they share the
 *     row and nothing scrolls.
 *   - Not enough: every column takes one comfortable width and the row
 *     scrolls. Never a squeeze in between — a column narrowed to fit wraps
 *     its buttons and cuts its names, and a row that overflows by a few
 *     dozen pixels shows a scrollbar whose thumb is the whole track.
 *
 * A board's columns can run long, which puts the browser's horizontal
 * scrollbar a screen or two below the column headings — exactly where nobody
 * is looking when they want to reach the last column. Flipping the scroll
 * container upside down and each column the right way up again moves the
 * native bar to the top, with nothing to keep in sync. Because the container
 * is flipped, `flex-end` on the cross axis is the visual top, and the padding
 * below the content is the visual gap under the bar.
 */
export function ScrollRow({
  children,
  gap = 14,
  minColumn = 210,
  scrollColumn = 240,
}: {
  children: ReactNode;
  gap?: number;
  /** The narrowest a column may be while every column still shares the row. */
  minColumn?: number;
  /** What every column is once the row has to scroll. */
  scrollColumn?: number;
}) {
  const row = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  const count = Children.count(children);

  useEffect(() => {
    const el = row.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setWidth(entry ? entry.contentRect.width : el.clientWidth);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Before the first measurement, assume it fits: the common case, and the
  // one that never shows a bar for a frame it then takes away.
  const fits = width === null || count === 0 || (width - gap * (count - 1)) / count >= minColumn;

  return (
    <div
      className="scroll-top"
      ref={row}
      style={{
        display: 'flex',
        gap,
        alignItems: 'flex-end',
        overflowX: 'auto',
        transform: 'scaleY(-1)',
        paddingBottom: 8,
      }}
    >
      {Children.map(children, (child) => (
        <div
          style={{
            display: 'flex',
            transform: 'scaleY(-1)',
            ...(fits
              ? { flex: '1 1 0', minWidth: 0 }
              : { flex: `0 0 ${scrollColumn}px`, width: scrollColumn }),
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
